import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { categoriasAPI } from '../services/api';
import { invalidateCategorias, invalidateTiposServicio } from '../services/cachedCatalogs';
import { Modal, Button, Input, Card, Badge, Tooltip, useConfirm } from './ui';

/**
 * Panel administrativo de Categorías de servicios.
 * Reutiliza los endpoints existentes categoriasAPI (mismos que usa el modal
 * en ServiciosPanel). Aqui viven como pagina dedicada para submenu.
 */

const emptyForm = { nombre: '', activo: true };

const CategoriasPanel = () => {
    const confirm = useConfirm();
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formModal, setFormModal] = useState({ open: false, editing: null, data: emptyForm });
    const [saving, setSaving] = useState(false);

    useEffect(() => { cargar(); }, []);

    const cargar = async () => {
        setLoading(true);
        try {
            const res = await categoriasAPI.getAll(true);
            const normalizadas = (res.data || []).map(c => ({ ...c, activo: !!c.activo }));
            setCategorias(normalizadas);
        } catch (err) {
            toast.error('No pudimos cargar las categorías');
        } finally {
            setLoading(false);
        }
    };

    const abrirCrear = () => setFormModal({ open: true, editing: null, data: { ...emptyForm } });
    const abrirEditar = (c) => setFormModal({
        open: true,
        editing: c.id,
        data: { nombre: c.nombre, activo: !!c.activo }
    });
    const cerrarForm = () => setFormModal({ open: false, editing: null, data: emptyForm });

    const handleGuardar = async () => {
        const { editing, data } = formModal;
        if (!data.nombre.trim()) return toast.error('El nombre es obligatorio');
        setSaving(true);
        try {
            const payload = { nombre: data.nombre.trim(), activo: data.activo };
            if (editing) {
                await categoriasAPI.update(editing, payload);
                toast.success('Categoría actualizada');
            } else {
                await categoriasAPI.create(payload);
                toast.success(`Categoría "${payload.nombre}" creada`);
            }
            invalidateCategorias();
            invalidateTiposServicio();
            cerrarForm();
            cargar();
        } catch (err) {
            toast.error(err.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActivo = async (c) => {
        try {
            await categoriasAPI.update(c.id, { activo: !c.activo });
            invalidateCategorias();
            invalidateTiposServicio();
            cargar();
        } catch (err) {
            toast.error(err.message || 'No pudimos cambiar el estado');
        }
    };

    const handleEliminar = async (c) => {
        const enUso = c.tipos_count > 0;
        if (enUso) {
            toast.error(`No se puede eliminar: ${c.tipos_count} servicio(s) usan esta categoría. Desactívala en su lugar.`);
            return;
        }
        const ok = await confirm({
            title: '¿Eliminar categoría?',
            message: <p>Se eliminará <strong>{c.nombre}</strong>. Esta acción no se puede deshacer.</p>,
            confirmText: 'Eliminar',
            danger: true
        });
        if (!ok) return;
        try {
            await categoriasAPI.delete(c.id);
            invalidateCategorias();
            toast.success('Categoría eliminada');
            cargar();
        } catch (err) {
            toast.error(err.message || 'No se pudo eliminar');
        }
    };

    if (loading) return <div className="loading">Cargando categorías...</div>;

    return (
    <>
        <div className="card">
            <div className="card-header">
                <h2>Categorías de servicios</h2>
            </div>
            <div className="admin-content">
                <div className="admin-section-header">
                    <div>
                        <h3 className="h3">Catálogo de categorías</h3>
                        <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                            Agrupan los servicios en el selector del formulario de cotización.
                        </p>
                    </div>
                    <Button variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={abrirCrear}>
                        Nueva categoría
                    </Button>
                </div>

                {categorias.length === 0 ? (
                    <div className="empty-state" style={{ padding: 32, textAlign: 'center', color: 'rgb(var(--muted))' }}>
                        No hay categorías configuradas. Crea la primera para empezar.
                    </div>
                ) : (
                <div className="prefijos-list">
                    {categorias.map(c => (
                        <Card key={c.id} className={`prefijo-card ${!c.activo ? 'is-inactive' : ''}`}>
                            <Card.Body className="prefijo-card-body">
                                <div className="prefijo-main">
                                    <div className="prefijo-info">
                                        <div className="prefijo-nombre">
                                            {c.nombre}
                                            {!c.activo && <Badge color="neutral" size="sm">Inactiva</Badge>}
                                        </div>
                                        <div className="prefijo-meta">
                                            {c.tipos_count || 0} servicio{c.tipos_count === 1 ? '' : 's'} en esta categoría
                                        </div>
                                    </div>
                                </div>
                                <div className="prefijo-actions">
                                    <Tooltip content={c.activo ? 'Desactivar' : 'Activar'}>
                                        <Button
                                            variant={c.activo ? 'ghost' : 'soft'}
                                            size="sm"
                                            leftIcon={c.activo ? <X size={13} /> : <Check size={13} />}
                                            onClick={() => handleToggleActivo(c)}
                                        >
                                            {c.activo ? 'Desactivar' : 'Activar'}
                                        </Button>
                                    </Tooltip>
                                    <Tooltip content="Editar">
                                        <Button variant="ghost" size="sm" leftIcon={<Pencil size={13} />} onClick={() => abrirEditar(c)}>
                                            Editar
                                        </Button>
                                    </Tooltip>
                                    <Tooltip content={c.tipos_count > 0 ? `No se puede eliminar: ${c.tipos_count} en uso` : 'Eliminar'}>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            leftIcon={<Trash2 size={13} />}
                                            onClick={() => handleEliminar(c)}
                                            disabled={c.tipos_count > 0}
                                        />
                                    </Tooltip>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                </div>
                )}

                <Modal
                    open={formModal.open}
                    onClose={cerrarForm}
                    title={formModal.editing ? 'Editar categoría' : 'Nueva categoría'}
                    size="md"
                    footer={
                        <>
                            <Button variant="ghost" size="sm" onClick={cerrarForm}>Cancelar</Button>
                            <Button variant="primary" size="sm" onClick={handleGuardar} disabled={saving}>
                                {saving ? 'Guardando...' : (formModal.editing ? 'Guardar cambios' : 'Crear categoría')}
                            </Button>
                        </>
                    }
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div>
                            <label className="label">Nombre</label>
                            <Input
                                value={formModal.data.nombre}
                                onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, nombre: e.target.value } }))}
                                placeholder="Ej: Diseño gráfico, Desarrollo web..."
                                maxLength={100}
                            />
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    </>
    );
};

export default CategoriasPanel;
